import { useEffect, useRef, useState } from "react"
import { Box, CloseButton, Dialog, HStack, Portal, Separator, Spacer, VStack } from "@chakra-ui/react"
import { ALL_STEPS } from "@/constants"
import { Deal } from "@/types"
import { Button, StepItem, Text } from "../ui"

interface Props {
  deals: Deal[]
  isOpen: boolean
  onClose: () => void
}

export const WhatsNextModal = ({ deals, isOpen, onClose }: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const activeStepRef = useRef<HTMLDivElement>(null)
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(deals[0] || null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  useEffect(() => {
    if (isOpen && activeStepRef.current && scrollContainerRef.current) {
      // slight timeout so modal fully renders before scrolling
      setTimeout(() => {
        activeStepRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }, 100)
    }
  }, [isOpen])

  useEffect(() => {
    if (deals && deals.length > 0) {
      setCurrentDeal(deals[0]);
      const index = ALL_STEPS.findIndex(step => step.step === deals[0].portalStage);
      setCurrentStepIndex(index !== -1 ? index : 0);
    }
  }, [deals]);

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.portalStage);
      setCurrentStepIndex(index !== -1 ? index : 0);
    }
  }, [currentDeal]);

  return (
    <Dialog.Root size="full" open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <VStack pt={10} px={10} pb={5} gap={5} flex="1" overflow="hidden" align="stretch">
              <Text fontSize="2xl" fontWeight="bold">Here's What's Next</Text>
              <HStack mt={5} gap={2}>
                {
                  deals && deals.map((deal, index) => (
                    <Button
                      key={index}
                      variant={ deal.id === currentDeal?.id ? "solid" : "outline"}
                      onClick={() => setCurrentDeal(deal)}
                    >
                      {deal.company.name}
                    </Button>
                  ))
                }
              </HStack>
              <Separator />
              <VStack ref={scrollContainerRef} overflowY="scroll" maxH="70vh">
                {
                  ALL_STEPS.map((step, index) => {
                    const ref = step.isActive ? activeStepRef : undefined
                    return (
                      <Box key={index} ref={ref} w="100%">
                        <StepItem
                          step={step}
                          isCurrent={index === currentStepIndex}
                          isCompleted={index < (currentStepIndex ?? 0)}
                          isLast={index === ALL_STEPS.length - 1}
                        />
                      </Box>
                    )
                  })
                }
              </VStack>
              <Spacer />
              <HStack justify="flex-end">
                <Button variant="outline" onClick={onClose}>Done</Button>
              </HStack>
            </VStack>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}